import React, { useState, useEffect, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import AInput from "views/pages/manage/common/formFeilds/customInput/AInput";
import CustomDatePickerV2 from "views/pages/manage/common/commonComponents/CustomDatePicker-V2";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import SectionCard from "./SectionCard";
import AgreementsSection from "./AgreementsSection";
import { IoArrowBack } from "react-icons/io5";
import { BsPersonLinesFill, BsClipboardData } from "react-icons/bs";
import { MdOutlineContactPage, MdOutlinePayment } from "react-icons/md";
import { RiUserSharedLine } from "react-icons/ri";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import { AiOutlineEdit } from "react-icons/ai";
import { CircularProgress } from "@mui/material";
import {
  inputHeight,
  getVendorFormInitialState,
  validateGstin,
  validateIfsc,
  validateEmail,
  buildVendorPayload,
  mapApiResponseToForm,
  verifyButtonStyle,
  occupationDropdown,
  bankAccountTypes,
  withholdingTaxTypeDropdown,
  receiptTypes,
} from "./utils";
import {
  createVendor,
  editVendor,
  getVendorById,
  useFetchDropdowns,
  verifyEmpId,
  verifyPinCode,
} from "./vendorService";
import {
  successToast,
  failureToast,
} from "views/pages/manage/common/utils/methods/toasterFunctions/function";
import moment from "moment";
import { UserDetails } from "appContext";
import { regex } from "views/pages/manage/common/constant";

// ─── Verified badge + Edit icon ───────────────────────────────────────
const VerifiedBadge = ({ onEdit }) => (
  <div className="d-flex align-items-center mt-1" style={{ gap: 8 }}>
    <span
      style={{
        fontSize: 12,
        color: "#00a854",
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      <span style={{ fontSize: 14 }}>✓</span> Verified
    </span>
    <AiOutlineEdit
      size={16}
      style={{ cursor: "pointer", color: "#00B0F0" }}
      onClick={onEdit}
      title="Edit"
    />
  </div>
);

// ─── Reusable field components ────────────────────────────────────────
const FormGroup = ({ children, col = "col-md-3" }) => (
  <div className={`form-group ${col} mb-3`}>{children}</div>
);

const FieldInput = ({
  label,
  inputKey,
  form,
  onChange,
  disabled,
  error,
  subInput,
  updatedStyle = {},
  ...rest
}) => (
  <FormGroup col={rest.col}>
    <AInput
      label={label}
      value={form[inputKey] ?? ""}
      handler={(v) => {
        onChange(inputKey, v);
      }}
      disabled={disabled}
      style={{
        ...updatedStyle,
        ...inputHeight,
        ...(disabled ? { background: "#F5F5F5" } : {}),
      }}
      {...rest}
    />
    {subInput}
    {error && <span style={{ color: "#d32f2f", fontSize: 11 }}>{error}</span>}
  </FormGroup>
);

const FieldDropdown = ({
  label,
  inputKey,
  form,
  onChange,
  options,
  disabled,
  error,
  ...rest
}) => (
  <FormGroup col={rest.col}>
    <AAutoComplete
      label={label}
      currentValue={form[inputKey]}
      items={options || []}
      isMulti={false}
      isDisabled={disabled}
      handleChange={(v) => onChange(inputKey, v)}
      height="44px"
      labelStyle={{ color: "#262626" }}
    />
    {error && <span style={{ color: "#d32f2f", fontSize: 11 }}>{error}</span>}
  </FormGroup>
);

const FieldDate = ({
  label,
  inputKey,
  form,
  onChange,
  disabled,
  error,
  ...rest
}) => (
  <FormGroup col={rest.col}>
    <CustomDatePickerV2
      placeHolder="Select date"
      value={form[inputKey]}
      handler={(index, date) => onChange(inputKey, date)}
      maxDate={new moment(new Date())}
      disabled={disabled}
      label={label}
    />
    {error && <span style={{ color: "#d32f2f", fontSize: 11 }}>{error}</span>}
  </FormGroup>
);

// ─── Read-only display value ─────────────────────────────────────────
const ReadOnlyField = ({ label, value, col = "col-md-3" }) => (
  <div className={`${col} mb-3`}>
    <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>{label}</div>
    <div style={{ fontWeight: 700, fontSize: 14, color: "#262626" }}>
      {value || "-"}
    </div>
  </div>
);

// ─── Main Form ────────────────────────────────────────────────────────
const CreateVendorForm = () => {
  const history = useHistory();
  const { dropdowns, isDropdownLoading } = useFetchDropdowns();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // ── Detect mode from query params ────────────────────────────────
  const vendorId = searchParams.get("vendorId");
  // edit mode
  const vendorTypeValue = searchParams.get("vendorType"); // create mode
  const isEditMode = !!vendorId;

  const modalData = {
    vendorType: vendorTypeValue
      ? {
          value: vendorTypeValue,
          label: searchParams.get("vendorTypeLabel") || vendorTypeValue,
        }
      : null,
    panCardNumber: searchParams.get("panCardNumber") || "",
  };

  const dd = dropdowns || {};
  const [form, setForm] = useState(getVendorFormInitialState(modalData));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [fetchingVendor, setFetchingVendor] = useState(isEditMode);
  const userDetails = useContext(UserDetails);

  // ── Fetch vendor data in edit mode ───────────────────────────────
  useEffect(() => {
    if (!isEditMode || !vendorId) return;

    const fetchVendor = async () => {
      setFetchingVendor(true);
      try {
        const res = await getVendorById(vendorId);
        if (res?.code === 200 && res?.data) {
          const prefilled = mapApiResponseToForm(res.data, dd);
          setForm(prefilled);
          // If pincode/empId already present, mark as verified
          // Mark as verified if values exist from API
          if (prefilled.pincode && prefilled.city) setPincodeVerified(true);
          if (prefilled.referenceEmployeeId && prefilled.referenceEmployeeName)
            setEmpIdVerified(true);
        } else {
          failureToast(res?.message || "Failed to fetch vendor details.");
        }
      } catch (err) {
        failureToast("Error fetching vendor details.");
        console.error(err);
      } finally {
        setFetchingVendor(false);
      }
    };

    fetchVendor();
  }, [vendorId]);

  // ── Verified states ───────────────────────────────────────────────
  const [pincodeVerified, setPincodeVerified] = useState(false);
  const [pincodeVerifying, setPincodeVerifying] = useState(false);
  const [empIdVerified, setEmpIdVerified] = useState(false);
  const [empIdVerifying, setEmpIdVerifying] = useState(false);

  // ── Pincode verify ────────────────────────────────────────────────
  const verifyPinCodeHandler = async () => {
    setPincodeVerifying(true);
    try {
      const success = await verifyPinCode(form.pincode, form, setForm, {
        cities: dd.cities || [],
        states: dd.states || [],
        countries: dd.countries || [],
      });
      if (!success) failureToast("Error occurred while verifying pin code");
      if (success) {
        setPincodeVerified(true);
        successToast("Pincode verified successfully");
      }
    } catch (error) {
      failureToast("Error occurred while verifying pin code");
      console.error(error);
    } finally {
      setPincodeVerifying(false);
    }
  };

  // ── Pincode Edit — re-enable and clear auto-filled fields ─────────
  const handlePincodeEdit = () => {
    setPincodeVerified(false);
    setForm((prev) => ({
      ...prev,
      pincode: "",
      city: null,
      state: null,
      country: null,
    }));
  };

  // ── Reference Emp ID verify ───────────────────────────────────────
  const verifyEmpIdHandler = async () => {
    setEmpIdVerifying(true);
    try {
      const success = await verifyEmpId(form.referenceEmployeeId, setForm);
      if (!success) failureToast("Error occurred while verifying employee ID");

      if (success) {
        setEmpIdVerified(true);
        successToast("Employee ID verified successfully");
      }
    } catch (error) {
      failureToast("Error occurred while verifying employee ID");
      console.error(error);
    } finally {
      setEmpIdVerifying(false);
    }
  };

  // ── Emp ID Edit — re-enable and clear auto-filled fields ──────────
  const handleEmpIdEdit = () => {
    setEmpIdVerified(false);
    setForm((prev) => ({
      ...prev,
      referenceEmployeeId: "",
      referenceEmployeeName: "",
      referenceDesignation: "",
    }));
  };

  const handleChange = (key, value) => {
    setForm((prev) => {
      const updated = { ...prev, [key]: value };

      // Auto-fill SAP Plant ID + employee on BU Code select
      if (key === "buCode") {
        updated.sapPlantId = value?.sapPlantId || "";
        updated.employeeId = userDetails?.employeeId || "";
        updated.employeeName =
          `${userDetails?.firstName || ""} ${userDetails?.lastName || ""}`.trim();
      }

      return updated;
    });

    // Reset verify state if user changes the field after verifying
    if (key === "pincode") setPincodeVerified(false);
    if (key === "referenceEmployeeId") setEmpIdVerified(false);

    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleAgreementsChange = (agreements) => {
    setForm((prev) => ({ ...prev, agreements }));
  };

  // ── Validation ────────────────────────────────────────────────────
  const runValidation = () => {
    const e = {};

    if (!form.onboardingDate) e.onboardingDate = "Onboarding date is required";

    // ── Name ──
    if (!form.firstName?.trim()) e.firstName = "First name is required";
    else if (form.firstName.length > 50)
      e.firstName = "Max 50 characters allowed";

    if (!form.lastName?.trim()) e.lastName = "Last name is required";
    else if (form.lastName.length > 50)
      e.lastName = "Max 50 characters allowed";

    // ── Email ──
    if (!form.emailId?.trim()) e.emailId = "Email is required";
    else if (!validateEmail(form.emailId)) e.emailId = "Invalid email format";

    // ── Mobile ──
    if (!form.mobileNumber?.trim())
      e.mobileNumber = "Mobile number is required";
    else if (!/^\d{10}$/.test(form.mobileNumber))
      e.mobileNumber = "Must be exactly 10 digits";

    // ── Language ──
    if (!form.language?.value) e.language = "Language is required";

    // ── Address ──
    if (!form.addressLine1?.trim())
      e.addressLine1 = "Address Line 1 is required";
    else if (form.addressLine1.length > 100)
      e.addressLine1 = "Max 100 characters allowed";

    if (form.addressLine2 && form.addressLine2.length > 100)
      e.addressLine2 = "Max 100 characters allowed";

    if (form.addressLine3 && form.addressLine3.length > 100)
      e.addressLine3 = "Max 100 characters allowed";

    if (form.addressLine4 && form.addressLine4.length > 100)
      e.addressLine4 = "Max 100 characters allowed";

    // ── Pincode ──
    if (!form.pincode) e.pincode = "Pincode is required";
    else if (!/^\d{6}$/.test(form.pincode))
      e.pincode = "Pincode must be 6 digits";

    // ── Occupation ──
    if (!form.occupation?.value) e.occupation = "Occupation is required";

    if (
      form.occupation?.value === "OTHER" &&
      !form.occupationManualEntry?.trim()
    )
      e.occupationManualEntry = "Please enter occupation details";
    else if (
      form.occupation?.value === "OTHER" &&
      form.occupationManualEntry.length > 20
    )
      e.occupationManualEntry = "Max 20 characters allowed";

    // ── Organisation ──
    if (!form.organisation?.trim()) e.organisation = "Organisation is required";
    else if (form.organisation.length > 50)
      e.organisation = "Max 50 characters allowed";

    // ── Designation ──
    if (!form.designation?.trim()) e.designation = "Designation is required";
    else if (form.designation.length > 20)
      e.designation = "Max 20 characters allowed";

    // ── GSTIN ──
    if (form.gstin) {
      if (form.gstin.length !== 15)
        e.gstin = "GSTIN must be exactly 15 characters";
      else if (!validateGstin(form.gstin)) e.gstin = "Invalid GSTIN format";
    }

    // ── BU ──
    if (!form.buCode?.value) e.buCode = "BU Code is required";

    // ── Payment ──
    if (!form.preferredPaymentMethod?.value)
      e.preferredPaymentMethod = "Required";

    if (!form.bankCode?.value) e.bankCode = "Bank code is required";

    if (!form.bankBranch?.trim()) e.bankBranch = "Bank branch is required";
    else if (form.bankBranch.length > 100)
      e.bankBranch = "Max 100 characters allowed";

    if (!form.accountHolderName?.trim())
      e.accountHolderName = "Account holder name is required";
    else if (form.accountHolderName.length > 100)
      e.accountHolderName = "Max 100 characters allowed";

    if (!form.accountNumber?.trim())
      e.accountNumber = "Account number is required";
    else if (!/^\d{9,18}$/.test(form.accountNumber))
      e.accountNumber = "Must be 9–18 digits";

    if (!form.ifscCode?.trim()) e.ifscCode = "IFSC is required";
    else if (form.ifscCode.length !== 11)
      e.ifscCode = "IFSC must be exactly 11 characters";
    else if (!validateIfsc(form.ifscCode))
      e.ifscCode = "Invalid IFSC format (e.g. ABCD0123456)";

    if (!form.accountType?.value) e.accountType = "Account type is required";

    // ── Agreements ──
    if (!form.agreements?.length)
      e.agreements = "At least one agreement is required";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!runValidation()) {
      setTimeout(() => {
        const firstErrorEl = document.querySelector(".vendor-field-error");
        if (firstErrorEl)
          firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      return;
    }

    setSubmitting(true);
    try {
      const reqBody = buildVendorPayload(form);

      const res = isEditMode
        ? await editVendor(reqBody, vendorId)
        : await createVendor(reqBody);

      if (res?.code === 200) {
        successToast(
          isEditMode
            ? "Vendor updated successfully."
            : "The creation request has been successfully completed and submitted for Level 1 (L1) approval. It is now pending review and further action from the designated approver.",
        );
        history.push("/admin/vendorsList");
      } else {
        failureToast(
          res?.message || "Failed to create vendor. Please try again.",
        );
      }
    } catch (error) {
      failureToast("Something went wrong. Please try again.");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Show loader while fetching vendor for edit
  if (fetchingVendor) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <CircularProgress size={32} />
        <span className="ml-3" style={{ color: "#555" }}>
          Loading vendor details...
        </span>
      </div>
    );
  }

  return (
    <div className="container-fluid p-3">
      {/* ── Page Header ── */}
      <div className="d-flex align-items-center mb-4">
        <IoArrowBack
          size={20}
          style={{ cursor: "pointer", marginRight: 10, color: "#262626" }}
          onClick={() => history.goBack()}
        />
        <h5
          style={{ fontWeight: 700, fontSize: 18, color: "#262626", margin: 0 }}
        >
          {isEditMode ? "Edit Vendor" : "Create New Vendor"}
        </h5>
      </div>

      {/* ── SECTION 1 – Vendor Details ── */}
      <SectionCard icon={<BsPersonLinesFill />} title="Vendor details">
        <div className="row mb-3">
          <ReadOnlyField label="Vendor Type" value={form.vendorType?.label} />
          <ReadOnlyField label="PAN Card Number" value={form.panCardNumber} />
          <ReadOnlyField
            label="SAP Vendor ID"
            value={form.sapVendorId || "-"}
          />
          <ReadOnlyField
            label="Approval Status"
            value={form.approvalStatus || "-"}
          />
        </div>
        <div className="row">
          <FieldDate
            label="Onboarding date *"
            inputKey="onboardingDate"
            form={form}
            onChange={handleChange}
            error={errors.onboardingDate}
          />
        </div>
      </SectionCard>

      {/* ── SECTION 2 – Basic Information ── */}
      <SectionCard icon={<BsClipboardData />} title="Basic Information">
        <div className="row">
          <FieldInput
            label="First Name *"
            inputKey="firstName"
            form={form}
            onChange={handleChange}
            placeholder="Enter first name"
            maxLength={50}
            error={errors.firstName}
          />
          <FieldInput
            label="Last Name *"
            inputKey="lastName"
            form={form}
            onChange={handleChange}
            placeholder="Enter last name"
            maxLength={50}
            error={errors.lastName}
          />
          <FieldInput
            label="Email ID *"
            inputKey="emailId"
            form={form}
            onChange={handleChange}
            placeholder="Enter email"
            error={errors.emailId}
          />
          <FieldInput
            label="Mobile Number *"
            inputKey="mobileNumber"
            form={form}
            onChange={handleChange}
            placeholder="Enter mobile"
            maxLength={10}
            error={errors.mobileNumber}
          />

          <FieldDropdown
            label="Language *"
            inputKey="language"
            form={form}
            onChange={handleChange}
            options={dd.languages || []}
            error={errors.language}
          />
          <FieldInput
            label="Address Line 1 *"
            inputKey="addressLine1"
            form={form}
            onChange={handleChange}
            placeholder="Enter address"
            maxLength={100}
            error={errors.addressLine1}
          />
          <FieldInput
            label="Address Line 2"
            inputKey="addressLine2"
            form={form}
            onChange={handleChange}
            placeholder="Enter address"
            maxLength={100}
          />
          <FieldInput
            label="Address Line 3"
            inputKey="addressLine3"
            form={form}
            onChange={handleChange}
            placeholder="Enter address"
            maxLength={100}
          />

          <FieldInput
            label="Address Line 4"
            inputKey="addressLine4"
            form={form}
            onChange={handleChange}
            placeholder="Enter address"
            maxLength={100}
          />

          {/* ── Pincode with Verify / Verified + Edit ── */}
          <FieldInput
            label="Pincode *"
            inputKey="pincode"
            form={form}
            onChange={handleChange}
            placeholder="Enter pincode"
            updatedStyle={{ width: "180px" }}
            disabled={pincodeVerified}
            error={errors.pincode}
            maxLength={6}
            subInput={
              pincodeVerified ? (
                <VerifiedBadge onEdit={handlePincodeEdit} />
              ) : (
                <AButton
                  updatedStyle={verifyButtonStyle}
                  variant="outline"
                  size="xs"
                  onClick={verifyPinCodeHandler}
                  className="mt-4 ml-0"
                  disabled={!form?.pincode || pincodeVerifying}
                >
                  {pincodeVerified ? (
                    <span
                      className="d-flex align-items-center"
                      style={{ gap: 4 }}
                    >
                      <CircularProgress size={10} color="inherit" />{" "}
                      Verifying...
                    </span>
                  ) : (
                    "Verify"
                  )}
                </AButton>
              )
            }
          />

          <FieldDropdown
            label="City"
            inputKey="city"
            form={form}
            onChange={handleChange}
            options={dd.cities || []}
            disabled={true}
          />
          <FieldDropdown
            label="State"
            inputKey="state"
            form={form}
            onChange={handleChange}
            options={dd.states || []}
            disabled={true}
          />
          <FieldDropdown
            label="Country"
            inputKey="country"
            form={form}
            onChange={handleChange}
            options={dd.countries || []}
            disabled={true}
          />

          <FieldDropdown
            label="Occupation *"
            inputKey="occupation"
            form={form}
            onChange={(key, value) => {
              handleChange(key, value);
              if (value?.value !== "OTHER")
                handleChange("occupationManualEntry", "");
            }}
            options={occupationDropdown || []}
            error={errors.occupation}
          />
          {form?.occupation?.value === "OTHER" && (
            <FieldInput
              label="Occupation (manual entry) *"
              inputKey="occupationManualEntry"
              form={form}
              onChange={handleChange}
              placeholder="Enter occupation"
              maxLength={20}
              error={errors.occupationManualEntry}
            />
          )}

          <FieldInput
            label="Organisation *"
            inputKey="organisation"
            form={form}
            onChange={handleChange}
            placeholder="Enter organisation"
            maxLength={50}
            error={errors.organisation}
          />
          <FieldInput
            label="Designation *"
            inputKey="designation"
            form={form}
            onChange={handleChange}
            placeholder="Enter designation"
            maxLength={20}
            error={errors.designation}
          />
          <FieldInput
            label="GSTIN"
            inputKey="gstin"
            form={form}
            onChange={(k, v) => handleChange(k, v?.toUpperCase())}
            placeholder="Enter GSTIN"
            maxLength={15}
            error={errors.gstin}
            regex={regex?.alphaNumeric}
          />
        </div>
      </SectionCard>

      {/* ── SECTION 3 – Onboarding Details ── */}
      <SectionCard icon={<MdOutlineContactPage />} title="Onboarding Details">
        <div className="row">
          <FieldDropdown
            label="BU Code *"
            inputKey="buCode"
            form={form}
            onChange={handleChange}
            options={dd.buCodes || []}
            error={errors.buCode}
          />
          <FieldInput
            label="SAP Plant ID"
            inputKey="sapPlantId"
            form={form}
            onChange={handleChange}
            disabled={true}
            placeholder="Auto-filled from BU Code"
          />
          <FieldInput
            label="Employee ID *"
            inputKey="employeeId"
            form={form}
            onChange={handleChange}
            disabled={true}
            updatedStyle={{ width: "180px" }}
            placeholder="Auto-filled from login"
          />
          <FieldInput
            label="Name"
            inputKey="employeeName"
            form={form}
            onChange={handleChange}
            disabled={true}
          />
          <FieldInput
            label="Designation"
            inputKey="employeeDesignation"
            form={form}
            onChange={handleChange}
            disabled={true}
          />
        </div>
      </SectionCard>

      {/* ── SECTION 4 – Reference Details ── */}
      <SectionCard icon={<RiUserSharedLine />} title="Reference Details">
        <div className="row">
          {/* ── Reference Emp ID with Verify / Verified + Edit ── */}
          <FieldInput
            label="Employee ID"
            inputKey="referenceEmployeeId"
            form={form}
            onChange={handleChange}
            updatedStyle={{ width: "185px" }}
            disabled={empIdVerified}
            error={errors.referenceEmployeeId}
            subInput={
              empIdVerified ? (
                <VerifiedBadge onEdit={handleEmpIdEdit} />
              ) : (
                <AButton
                  updatedStyle={verifyButtonStyle}
                  variant="outline"
                  size="xs"
                  onClick={verifyEmpIdHandler}
                  className="mt-4 ml-0"
                  disabled={!form?.referenceEmployeeId || empIdVerifying}
                >
                  {empIdVerifying ? (
                    <span
                      className="d-flex align-items-center"
                      style={{ gap: 4 }}
                    >
                      <CircularProgress size={10} color="inherit" />{" "}
                      Verifying...
                    </span>
                  ) : (
                    "Verify ID"
                  )}
                </AButton>
              )
            }
          />
          <FieldInput
            label="Name"
            inputKey="referenceEmployeeName"
            form={form}
            onChange={handleChange}
            disabled={true}
          />
          <FieldInput
            label="Designation"
            inputKey="referenceDesignation"
            form={form}
            onChange={handleChange}
            disabled={true}
          />
        </div>
      </SectionCard>

      {/* ── SECTION 5 – Payments and Tax ── */}
      <SectionCard icon={<MdOutlinePayment />} title="Payments and Tax">
        <div className="row">
          <FieldDropdown
            label="Preferred Payment Method *"
            inputKey="preferredPaymentMethod"
            form={form}
            onChange={handleChange}
            options={dd.paymentMethods || []}
            error={errors.preferredPaymentMethod}
          />
          <FieldDropdown
            label="Bank Code *"
            inputKey="bankCode"
            form={form}
            onChange={handleChange}
            options={dd.bankCodes || []}
            error={errors.bankCode}
          />
          <FieldInput
            label="Bank Branch *"
            inputKey="bankBranch"
            form={form}
            onChange={handleChange}
            placeholder="Enter bank branch"
            maxLength={100}
            error={errors.bankBranch}
          />
          <FieldInput
            label="Account Holder Name *"
            inputKey="accountHolderName"
            form={form}
            onChange={handleChange}
            placeholder="Enter account holder name"
            maxLength={100}
            error={errors.accountHolderName}
            regex={regex?.alphabetsWithSpace}
          />
          <FieldInput
            label="Account Number *"
            inputKey="accountNumber"
            form={form}
            onChange={handleChange}
            placeholder="Enter account number"
            maxLength={18}
            error={errors.accountNumber}
            regex={regex?.numeric}
          />
          <FieldInput
            label="IFSC Code *"
            inputKey="ifscCode"
            form={form}
            onChange={(k, v) => handleChange(k, v?.toUpperCase())}
            placeholder="ABCD0123456"
            maxLength={11}
            error={errors.ifscCode}
          />
          <FieldDropdown
            label="Account Type *"
            inputKey="accountType"
            form={form}
            onChange={handleChange}
            options={bankAccountTypes || []}
            error={errors.accountType}
          />
        </div>
      </SectionCard>

      {/* ── SECTION 6 – Withholding Tax Data ── */}
      <SectionCard
        icon={<HiOutlineOfficeBuilding />}
        title="Withholding Tax Data"
      >
        <div className="row">
          <FieldInput
            disabled={true}
            label="Title"
            inputKey="withholdingTitle"
            form={form}
            onChange={handleChange}
            placeholder="Enter title"
          />
          <FieldDropdown
            disabled={true}
            label="Withholding Tax Type"
            inputKey="withholdingTaxType"
            form={form}
            onChange={handleChange}
            options={withholdingTaxTypeDropdown}
          />
          <FieldInput
            disabled={true}
            label="Withholding Tax Code"
            inputKey="withholdingTaxCode"
            form={form}
            onChange={handleChange}
            placeholder="Enter code"
          />
          <FieldInput
            disabled={true}
            label="Default Jurisdiction"
            inputKey="defaultJurisdiction"
            form={form}
            onChange={handleChange}
            placeholder="Enter jurisdiction"
          />
          <FieldDropdown
            disabled={true}
            label="Recipient Type"
            inputKey="recipientType"
            form={form}
            onChange={handleChange}
            options={receiptTypes || []}
          />
          <FieldInput
            disabled={true}
            label="Payment Term"
            inputKey="paymentTerm"
            form={form}
            onChange={handleChange}
            placeholder="Enter term"
          />
          <FieldInput
            disabled={true}
            label="Account Group"
            inputKey="accountGroup"
            form={form}
            onChange={handleChange}
            placeholder="Enter group"
          />
          <FieldInput
            disabled={true}
            label="Vendor Class"
            inputKey="vendorClass"
            form={form}
            onChange={handleChange}
            placeholder="Enter class"
          />
        </div>
      </SectionCard>

      {/* ── SECTION 7 – Agreements ── */}
      <AgreementsSection
        agreements={form.agreements}
        onChange={handleAgreementsChange}
      />
      {errors.agreements && (
        <div
          className="vendor-error mb-3"
          style={{ color: "#d32f2f", fontSize: 12 }}
        >
          ⚠ {errors.agreements}
        </div>
      )}

      {/* ── Footer ── */}
      <div
        className="d-flex justify-content-end py-3"
        style={{
          background: "#fff",
          borderTop: "1px solid #E0E0E0",
          position: "sticky",
          bottom: 0,
          zIndex: 10,
        }}
      >
        <AButton
          onClick={() => history.goBack()}
          variant="outline"
          className="mr-2"
        >
          Cancel
        </AButton>
        <AButton
          onClick={handleSubmit}
          variant="primary_filled"
          className="button_remove_left_margin"
          disabled={submitting}
        >
          {submitting ? (
            <span className="d-flex align-items-center" style={{ gap: 6 }}>
              <CircularProgress size={14} color="inherit" />
              Creating...
            </span>
          ) : isEditMode ? (
            "Update Vendor"
          ) : (
            "Create Vendor"
          )}
        </AButton>
      </div>
    </div>
  );
};

export default CreateVendorForm;
